import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ExpandableSection,
  Form,
  Modal,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { useUserAlias } from 'src/common/hooks/useUserAlias';
import EmptyState from 'src/components/common/EmptyState/EmptyState';
import { renderInputField } from './utils/bulkEditInputs';
import { getColumnDefinitions } from './utils/bulkEditColumns';
import { BulkEditModalProps, saveBulkEditChanges, getUpdatedRecords } from './utils/bulkEditUtils';

/**
 * A modal component for bulk editing multiple rows of data
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.visible - Controls modal visibility
 * @param {Function} props.onDismiss - Callback when modal is dismissed
 * @param {Array<any>} props.selectedRows - Array of selected rows to edit
 * @param {Array<AttributeMetadata>} props.attributesMetadata - Metadata describing editable fields
 * @param {string} props.controlId - ID of the control being edited
 * @param {string} props.controlDatasetKey - Key field for the control dataset
 * @param {Function} [props.onSaveSuccess] - Optional callback when save is successful
 * @param {Function} [props.clearGridSelection] - Optional callback to clear grid selection
 * @returns {JSX.Element} The rendered modal component
 */
export const BulkEditModal: React.FC<BulkEditModalProps> = ({
  visible,
  onDismiss,
  selectedRows,
  attributesMetadata,
  controlId,
  controlDatasetKey,
  onSaveSuccess,
  clearGridSelection,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState<Record<string, any>>({});
  const [previewRecords, setPreviewRecords] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { alias } = useUserAlias();

  useEffect(() => {
    if (visible) {
      setEditData({});
      setPreviewRecords([]);
      setError(null);
    }
  }, [visible]);

  const editableAttributes = useMemo(
    () => attributesMetadata?.filter((attr) => attr.isEditable) || [],
    [attributesMetadata]
  );

  // Update preview records whenever editData changes
  useEffect(() => {
    const changesToApply = Object.entries(editData).reduce(
      (acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>
    );

    if (Object.keys(changesToApply).length === 0) {
      setPreviewRecords([]);
      return;
    }

    const updatedRecords = selectedRows.map((row) => {
      const originalValuesForChangedFields = Object.keys(changesToApply).reduce(
        (acc, key) => {
          acc[key] = row[key];
          return acc;
        },
        {} as Record<string, any>
      );

      return {
        ...row,
        ...changesToApply,
        _originalValues: originalValuesForChangedFields,
      };
    });

    setPreviewRecords(updatedRecords);
  }, [editData, selectedRows]);

  const columnDefinitions = useMemo(
    () => getColumnDefinitions({ attributesMetadata, editData, controlDatasetKey, alias }),
    [attributesMetadata, editData, controlDatasetKey, alias]
  );

  const isSaveDisabled = useMemo(() => {
    return isSaving || Object.values(editData).every((value) => value === undefined);
  }, [isSaving, editData]);

  const handleSave = async () => {
    if (!controlId || !controlDatasetKey) {
      setError('Cannot save: Missing controlId or controlDatasetKey');
      return;
    }

    const actualEditData = Object.entries(editData).reduce(
      (acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>
    );

    if (Object.keys(actualEditData).length === 0) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await saveBulkEditChanges({
        selectedRows,
        actualEditData,
        controlId,
        controlDatasetKey,
        alias,
      });

      if (clearGridSelection) {
        clearGridSelection();
      }

      if (onSaveSuccess) {
        const updatedTableRecords = getUpdatedRecords({
          selectedRows,
          actualEditData,
          alias,
        });
        onSaveSuccess(updatedTableRecords);
      }

      onDismiss();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred while saving changes');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Bulk Edit"
      size="max"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaveDisabled}
              loading={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert
            type="error"
            statusIconAriaLabel="Error"
            header="Error"
            dismissible
            onDismiss={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        <Alert
          statusIconAriaLabel="Warning"
          type="warning"
          header={
            <SpaceBetween direction="horizontal" size="xxs">
              <Box variant="awsui-key-label">Editing</Box>
              <Box color="text-body-secondary">· Selected items: </Box>
              <Box variant="awsui-key-label">{selectedRows.length}</Box>
            </SpaceBetween>
          }
        >
          <Box variant="p" color="text-body-secondary">
            Please confirm your changes before saving.
          </Box>
        </Alert>
        <ExpandableSection variant="container" headerText="Editable fields" defaultExpanded>
          <Form>
            <SpaceBetween size="m">
              {editableAttributes.length > 0 ? (
                editableAttributes.map((attr) =>
                  renderInputField({
                    attr,
                    editData,
                    setEditData,
                    isSaving,
                  })
                )
              ) : (
                <Box variant="p" color="text-status-info" textAlign="center">
                  No fields are configured as editable for this control dataset.
                </Box>
              )}
            </SpaceBetween>
          </Form>
        </ExpandableSection>
        <ExpandableSection variant="container" headerText="Preview changes" defaultExpanded={false}>
          <Table
            items={previewRecords}
            columnDefinitions={columnDefinitions}
            variant="embedded"
            empty={
              <EmptyState
                title={'No changes specified'}
                description={'Enter new values in the form above to preview the updates here.'}
              />
            }
          />
        </ExpandableSection>
      </SpaceBetween>
    </Modal>
  );
};
